import React, { useState } from 'react';
import api from '../services/api';
import { compressImage, resolveUploadUrl } from '../utils/helpers';
import { showError } from './ToastNotifications';
import ImageAnnotateModal from './ImageAnnotateModal';
import './ImageThumbStrip.css';

// A strip of ALREADY-UPLOADED images (urls) with, per image: open full size,
// ✏️ Draw (built-in editor → the marked copy is compressed, uploaded to
// `endpoint` and replaces that url) and ✕ Remove. Used wherever a form keeps
// a SET of uploaded images (KYC documents). `onChange(nextUrls)`.
export default function ImageThumbStrip({ urls, onChange, endpoint = '/upload/kyc', compressionOptions, altPrefix = 'Image', disabled, removeTitle = 'Remove this image' }) {
  const [drawIdx, setDrawIdx] = useState(null);
  const [busyIdx, setBusyIdx] = useState(null);
  const list = Array.isArray(urls) ? urls : [];
  if (!list.length) return null;

  const saveDrawn = async (file) => {
    const idx = drawIdx;
    setDrawIdx(null);
    if (idx == null) return;
    setBusyIdx(idx);
    try {
      let uploadFile = file;
      try { const { file: c } = await compressImage(file, compressionOptions || {}); uploadFile = c; } catch (_) { /* upload the original */ }
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!res.data || !res.data.url) throw new Error('No URL returned from server');
      onChange(list.map((u, i) => (i === idx ? res.data.url : u)));
    } catch (e) {
      showError(e.response?.data?.error || e.message || 'Could not save the drawing');
    } finally {
      setBusyIdx(null);
    }
  };

  return (
    <>
      <div className="img-strip">
        {list.map((url, idx) => (
          <div className="img-strip__thumb" key={`${url}-${idx}`}>
            <a href={resolveUploadUrl(url)} target="_blank" rel="noopener noreferrer" title="Open full size">
              <img src={resolveUploadUrl(url)} alt={`${altPrefix} ${idx + 1}`} />
            </a>
            {busyIdx === idx && <div className="img-strip__busy">Saving…</div>}
            {!disabled && (
              <>
                <button type="button" className="img-strip__draw" title="Draw on this image" aria-label={`Draw on ${altPrefix} ${idx + 1}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDrawIdx(idx); }} disabled={busyIdx != null}>✏️</button>
                <button type="button" className="img-strip__remove" title={removeTitle} aria-label={`Remove ${altPrefix} ${idx + 1}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(list.filter((_, i) => i !== idx)); }} disabled={busyIdx != null}>&times;</button>
              </>
            )}
          </div>
        ))}
      </div>
      <ImageAnnotateModal
        isOpen={drawIdx != null}
        source={drawIdx != null ? resolveUploadUrl(list[drawIdx]) : null}
        fileName={drawIdx != null ? String(list[drawIdx]).split('/').pop() : 'image'}
        onCancel={() => setDrawIdx(null)}
        onSave={saveDrawn}
      />
    </>
  );
}
